import { Component, OnInit, inject } from '@angular/core';
import { RouterLink, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageTitleService } from '../../core/services/page-title.service';
import { StudioService } from '../../core/services/studio.service';

@Component({
    selector: 'app-studio-fee',
    imports: [RouterLink, FormsModule],
    template: `
<div class="animated fadeIn">
  <div class="row sfapp-container">
    <div class="col-md-12">
      <div class="card">
        <div class="card-header">
          <span><i class="fa s-icon-edit"></i>&nbsp;編集</span>
        </div>
        @if (studio.studioFees?.length === 0) {
          <div class="card-body">
            <div class="row">
              <div class="col-md-12">
                @if (!alertDismissed) {
                <div class="alert alert-danger" role="alert">
                  <button type="button" class="close" (click)="alertDismissed = true"><span aria-hidden="true">&times;</span></button>
                  <p>会費清算で使用するデータが作成されていません。新規作成ボタンをクリックして作成してください。</p>
                </div>
                }
              </div>
              <div class="col-md-12">
                <button type="button" class="btn btn-primary text-white" (click)="initiateStudioFee()" [disabled]="initDisabled">
                  <i class="fa fa-plus-square-o"></i>&nbsp;新規作成
                </button>
              </div>
            </div>
          </div>
        }
        <div class="card-body">
          <div class="col-md-12">
            <div class="form-group row">
              <div class="col-md-6">
                <table class="table-bordered table-sm">
                  <thead>
                    <tr><th width="50%">表示ID</th><th width="50%">店舗名</th></tr>
                  </thead>
                  <tbody>
                    <tr><td>{{studio.sortBy}}</td><td>{{studio.name}}</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
            @if (loading) {
              <div class="text-center py-3"><span class="fa fa-spinner fa-spin fa-2x"></span></div>
            }
            @for (fee of studio.studioFees; track fee.id) {
              <div class="row">
                <div class="form-group" [class]="fee.type !== 'settlement_transfer_unit_fee' ? 'col-md-6' : 'col-md-4'">
                  <label class="col-form-label">{{fee.name}}</label>
                  <input type="text" class="form-control" [ngModel]="fee.type === 'credit_card_use_ratio' ? fee.value : integerFeeLabel(fee.value)" (ngModelChange)="fee.value = $event" [name]="'fee_' + fee.id" [pattern]="asciiPattern">
                </div>
                @if (fee.type === 'settlement_transfer_unit_fee') {
                  <div class="form-group col-md-2">
                    <label class="col-form-label">税額</label>
                    <input type="text" class="form-control" [ngModel]="integerFeeLabel(fee.taxAmount)" (ngModelChange)="fee.taxAmount = $event" [name]="'tax_' + fee.id" [pattern]="asciiPattern">
                  </div>
                }
                <div class="form-group col-md-6 d-flex align-items-end">
                  @if (fee.type !== 'settlement_transfer_unit_fee') {
                    <button type="button" class="btn btn-success" (click)="updateFee(fee.id, fee.value)">
                      <i class="fa s-icon-save"></i>&nbsp;更新
                    </button>
                  }
                  @if (fee.type === 'settlement_transfer_unit_fee') {
                    <button type="button" class="btn btn-success" (click)="updateFee(fee.id, fee.value, fee.taxAmount)">
                      <i class="fa s-icon-save"></i>&nbsp;更新
                    </button>
                  }
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
  `
})
export class StudioFeeComponent implements OnInit {
  private pageTitleService = inject(PageTitleService);
  private studioService = inject(StudioService);
  private route = inject(ActivatedRoute);

  studioId!: number;
  studio: any = { studioFees: [] };
  loading = true;
  alertDismissed = false;
  initDisabled = false;
  readonly asciiPattern = /^[\x01-\x7E]+$/;

  integerFeeLabel(value: unknown): string {
    if (value == null || value === '') return '';
    const integer = Number.parseInt(String(value), 10);
    return Number.isFinite(integer) ? String(integer) : String(value);
  }

  async ngOnInit(): Promise<void> {
    this.pageTitleService.set('店舗管理', 's-icon-studio');
    this.studioId = +this.route.snapshot.params['studioId'];
    await this.loadStudio();
  }

  private async loadStudio(): Promise<void> {
    this.loading = true;
    try {
      const data: any = await this.studioService.list(this.studioId);
      this.studio = Array.isArray(data) ? (data[0] ?? {}) : (data ?? {});
      if (!this.studio.studioFees) this.studio.studioFees = [];
    } finally {
      this.loading = false;
    }
  }

  async updateFee(id: number, value: any, taxAmount: any = null): Promise<void> {
    await this.studioService.upsertStudioFee({ id, value, taxAmount });
    await this.loadStudio();
  }

  async initiateStudioFee(): Promise<void> {
    this.initDisabled = true;
    const initialFees = [
      { studioId: this.studioId, name: 'ロイヤリティ費', type: 'royalty_fee', value: 0 },
      { studioId: this.studioId, name: 'システム利用料', type: 'system_usage_fee', value: 0 },
      { studioId: this.studioId, name: '警備費', type: 'alsoc_security_fee', value: 0 },
      { studioId: this.studioId, name: '販促支援金', type: 'advertise_fee', value: 0 },
      { studioId: this.studioId, name: 'りそなネット基本料', type: 'risona_net_use_fee', value: 0 },
      { studioId: this.studioId, name: 'ペイジー端末利用基本料', type: 'payeasy_device_use_fee', value: 0 },
      { studioId: this.studioId, name: '口座登録手数料', type: 'account_register_unit_fee', value: 225 },
      { studioId: this.studioId, name: '口座引落手数料', type: 'bank_transfer_unit_fee', value: 80 },
      { studioId: this.studioId, name: 'GMO取引処理料', type: 'gmo_use_unit_fee', value: 6 },
      { studioId: this.studioId, name: 'クレジットカード決済手数料', type: 'credit_card_use_ratio', value: 3.15 },
      {
        studioId: this.studioId,
        name: '精算金振込手数料',
        type: 'settlement_transfer_unit_fee',
        value: 260,
        taxAmount: 25,
      },
    ];
    for (const fee of initialFees) {
      await this.studioService.upsertStudioFee(fee);
    }
    await this.loadStudio();
    this.initDisabled = false;
  }
}
